(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-ms-subscription-widget]';
  const instances = new Set();

  const delay = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds));

  const formatWithDelimiters = (cents, precision = 2, thousands = ',', decimal = '.') => {
    const number = Number(cents) / 100;
    if (!Number.isFinite(number)) return '0';

    const parts = number.toFixed(precision).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
  };

  const formatMoney = (cents, format) => {
    const template = format || '{{amount}}';
    const match = template.match(/{{\s*(\w+)\s*}}/);
    const token = match ? match[1] : 'amount';
    let value;

    switch (token) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return template.replace(match ? match[0] : '{{amount}}', value);
  };

  class MsSubscriptionWidget {
    constructor(root) {
      if (root.dataset.msSubscriptionInitialized === 'true') return;
      root.dataset.msSubscriptionInitialized = 'true';

      this.root = root;
      instances.add(this);
      this.section = root.closest('[id^="MainProduct-"]');
      this.productInfo = root.closest('product-info');
      this.sectionId = root.dataset.sectionId;
      this.productId = String(root.dataset.productId || '');
      this.discount = Math.min(100, Math.max(0, Number.parseFloat(root.dataset.discount) || 0));
      this.moneyFormat = root.dataset.moneyFormat || '{{amount}}';
      this.mode = 'one_time';
      this.syncToken = 0;
      this.resyncing = false;
      this.sealTarget = null;
      this.sealAttempts = 0;

      try {
        this.variants = JSON.parse(root.querySelector('[data-ms-subscription-variants]').textContent);
      } catch (error) {
        this.variants = [];
        console.error('[MS subscription] Variant JSON could not be read.', error);
      }

      try {
        this.duplicateVariants = JSON.parse(root.querySelector('[data-ms-duplicate-variants]')?.textContent || '[]');
      } catch (error) {
        this.duplicateVariants = [];
        console.error('[MS subscription] Duplicate variant JSON could not be read.', error);
      }

      this.purchaseInputs = Array.from(root.querySelectorAll('.ms-subscription__input'));
      this.frequency = root.querySelector('[data-ms-subscription-frequency]');
      this.status = root.querySelector('[data-ms-subscription-status]');

      this.purchaseInputs.forEach((input) => {
        input.checked = input.value === this.mode;
        input.addEventListener('change', () => this.selectPurchase(input.value));
      });
      this.frequency.addEventListener('change', () => this.changeFrequency());

      this.setPending(true);
      this.refreshReferences();
      this.bindGuards();
      this.bindVariantChanges();
      this.observeReplacements();
      this.observePriceOverrides();
      this.updateAllPrices();
      this.connectSeal();
    }

    refreshReferences() {
      this.form =
        this.section?.querySelector('product-form[data-main="true"] form[data-type="add-to-cart-form"]') ||
        this.section?.querySelector('form[data-type="add-to-cart-form"]');
      this.variantSelects = this.productInfo?.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
      this.variantIdInput = this.form?.querySelector('input.product-variant-id[name="id"]');
      this.form?.closest('product-form')?.parentElement?.classList.add('ms-subscription__buttons-block');
    }

    bindGuards() {
      if (!this.section || this.section.dataset.msSubscriptionGuards === 'true') return;
      this.section.dataset.msSubscriptionGuards = 'true';

      this.section.addEventListener(
        'click',
        (event) => {
          const button = event.target.closest(
            'button[name="add"], .product-form__submit, .sticky-atc__add_button, .shopify-payment-button__button'
          );
          if (!button || !this.section.contains(button)) return;
          if (this.canSubmit()) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          this.showStatus('יש לבחור רכישה חד-פעמית או מנוי לפני ההוספה לסל.');
          this.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
        true
      );

      this.section.addEventListener(
        'submit',
        (event) => {
          if (!event.target.matches('form[data-type="add-to-cart-form"]') || this.canSubmit()) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showStatus('יש לבחור רכישה חד-פעמית או מנוי לפני ההוספה לסל.');
        },
        true
      );
    }

    bindVariantChanges() {
      this.productInfo?.addEventListener('change', (event) => {
        if (!event.target.closest('variant-selects')) return;
        this.scheduleVariantRefresh();
      });
    }

    observeReplacements() {
      if (!this.productInfo) return;

      this.replacementObserver = new MutationObserver((records) => {
        const relevantChange = records.some((record) =>
          Array.from(record.addedNodes).some(
            (node) =>
              node.nodeType === 1 &&
              (node.matches?.('.sealsubs-target-element, .sls-option, form[data-type="add-to-cart-form"]') ||
                node.querySelector?.('.sealsubs-target-element, .sls-option, form[data-type="add-to-cart-form"]'))
          )
        );
        if (!relevantChange) return;

        this.refreshReferences();
        this.connectSeal();
      });

      this.replacementObserver.observe(this.productInfo, { childList: true, subtree: true });
    }

    observePriceOverrides() {
      const observerRoot = this.section || this.productInfo;
      if (!observerRoot) return;

      const priceRegionSelector = [
        '.product-form__input--quantity-breaks',
        '.sticky-atc__price',
        '[id^="main-price-"]',
        '[class*="main-atc-price-span-"]'
      ].join(', ');

      this.priceObserver = new MutationObserver((records) => {
        if (!this.mode || this.priceRefreshQueued) return;

        const relevantChange = records.some((record) => {
          const targetElement = record.target.nodeType === 1 ? record.target : record.target.parentElement;
          if (targetElement?.closest?.(priceRegionSelector)) return true;

          return Array.from(record.addedNodes).some(
            (node) =>
              node.nodeType === 1 &&
              (node.matches?.(priceRegionSelector) || node.querySelector?.(priceRegionSelector))
          );
        });
        if (!relevantChange) return;

        this.priceRefreshQueued = true;
        window.requestAnimationFrame(() => {
          this.priceRefreshQueued = false;
          this.refreshReferences();
          this.updateAllPrices();
        });
      });

      this.priceObserver.observe(observerRoot, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    findSealTarget() {
      const targets = Array.from(this.productInfo?.querySelectorAll('.sealsubs-target-element') || []);
      return (
        targets.find((target) => String(target.dataset.productId || '') === this.productId) ||
        targets.find((target) => target.querySelector('.sls-option')) ||
        null
      );
    }

    connectSeal(shouldResync = true) {
      const target = this.findSealTarget();
      if (!target || !target.querySelector('.sls-option')) {
        if (this.sealAttempts < 60) {
          this.sealAttempts += 1;
          window.clearTimeout(this.sealRetryTimer);
          this.sealRetryTimer = window.setTimeout(() => this.connectSeal(), 250);
        } else {
          this.showStatus('אפשרויות המנוי לא נטענו. יש לרענן את העמוד ולנסות שוב.');
        }
        return;
      }

      this.sealTarget = target;
      this.sealAttempts = 0;
      window.clearTimeout(this.sealRetryTimer);
      target.closest('.shopify-app-block')?.classList.add('ms-subscription__seal-source');
      this.populateFrequencies();
      this.updateSealModeBenefits();
      this.root.classList.add('is-seal-ready');

      if (this.mode && shouldResync && !this.resyncing) {
        this.resyncCurrentMode();
      } else {
        if (!this.mode) this.clearSealForPendingState();
      }
    }

    clearSealForPendingState() {
      const oneTime = this.getSealRadio('one_time');
      if (!oneTime) return;
      const wasChecked = oneTime.checked;
      oneTime.click();
      if (wasChecked) oneTime.dispatchEvent(new Event('change', { bubbles: true }));
    }

    getSealRadio(mode) {
      if (!this.sealTarget?.isConnected) this.sealTarget = this.findSealTarget();
      return this.sealTarget?.querySelector(`input.sls-option[value="${mode}"]`) || null;
    }

    getSealFrequency() {
      if (!this.sealTarget?.isConnected) this.sealTarget = this.findSealTarget();
      return this.sealTarget?.querySelector('select.sls-select[name="subs_interval"], select.sls-select') || null;
    }

    populateFrequencies() {
      const sealSelect = this.getSealFrequency();
      if (!sealSelect || !this.frequency) return;

      const previousValue = this.frequency.value;
      const signature = Array.from(sealSelect.options)
        .map((option) => `${option.value}\u0000${option.textContent.trim()}`)
        .join('\u0001');
      if (this.frequency.dataset.optionsSignature !== signature) {
        this.frequency.replaceChildren(
          ...Array.from(sealSelect.options).map((sourceOption) => {
            const option = document.createElement('option');
            option.value = sourceOption.value;
            option.textContent = sourceOption.textContent;
            option.disabled = sourceOption.disabled;
            return option;
          })
        );
        this.frequency.dataset.optionsSignature = signature;
      }

      this.frequency.value =
        Array.from(this.frequency.options).some((option) => option.value === previousValue)
          ? previousValue
          : sealSelect.value;
      this.frequency.disabled = this.mode !== 'subscription';
    }

    async selectPurchase(mode) {
      if (mode !== 'one_time' && mode !== 'subscription') return;

      const token = ++this.syncToken;
      this.resyncing = true;
      this.root.classList.add('is-busy');
      this.clearStatus();

      const synchronized = await this.syncSeal(mode);
      if (token !== this.syncToken) return;

      this.resyncing = false;
      this.root.classList.remove('is-busy');

      if (!synchronized) {
        this.mode = null;
        this.purchaseInputs.forEach((input) => (input.checked = false));
        this.frequency.disabled = true;
        this.setPending(true);
        this.showStatus('אפשרויות המנוי עדיין נטענות. יש לנסות שוב בעוד רגע.');
        return;
      }

      this.mode = mode;
      if (!this.setFormVariantForMode(mode)) {
        this.mode = null;
        this.purchaseInputs.forEach((input) => (input.checked = false));
        this.frequency.disabled = true;
        this.setPending(true);
        this.showStatus('לא נמצאה גרסה תואמת וזמינה למוצר הרכישה שנבחר.');
        return;
      }
      this.frequency.disabled = mode !== 'subscription';
      this.setPending(false);
      this.updateAllPrices();
    }

    async syncSeal(mode) {
      this.refreshReferences();
      this.connectSeal(false);

      /* Seal belongs to the visible/original product, so restore its variant
       * before asking Seal to change purchase mode or frequency. */
      if (!this.setFormVariantForMode('subscription')) return false;

      const radio = this.getSealRadio(mode);
      if (!radio || !this.form) return false;

      const wasChecked = radio.checked;
      radio.click();
      if (wasChecked) radio.dispatchEvent(new Event('change', { bubbles: true }));

      if (mode === 'subscription') {
        this.populateFrequencies();
        const sealSelect = this.getSealFrequency();
        if (sealSelect) {
          const requestedValue = this.frequency.value || sealSelect.value;
          if (Array.from(sealSelect.options).some((option) => option.value === requestedValue)) {
            sealSelect.value = requestedValue;
          }
          sealSelect.dispatchEvent(new Event('input', { bubbles: true }));
          sealSelect.dispatchEvent(new Event('change', { bubbles: true }));
          this.frequency.value = sealSelect.value;
        }
      }

      /* Seal can take several seconds to add its cart properties. */
      for (let attempt = 0; attempt < 80; attempt += 1) {
        await delay(100);
        if (this.isSynchronized(mode)) {
          this.updateSealModeBenefits();
          return this.discountMatchesSeal(mode);
        }
      }
      return false;
    }

    async changeFrequency() {
      if (this.mode !== 'subscription') return;
      this.resyncing = true;
      this.clearStatus();

      const synchronized = await this.syncSeal('subscription');
      this.resyncing = false;
      if (synchronized) return;

      this.setPending(true);
      this.showStatus('לא ניתן לעדכן את תדירות המנוי כרגע. יש לנסות שוב.');
    }

    formControls() {
      return this.form ? Array.from(this.form.elements) : [];
    }

    hasSellingPlan() {
      return this.formControls().some((control) => control.name === 'selling_plan' && String(control.value || '').trim());
    }

    hasSealProperties() {
      return this.formControls().some(
        (control) => {
          const name = String(control.name || '');
          return Boolean(name) && (
            control.classList?.contains('sls-extension-added-attribute') ||
            /^properties\[.*_sealsub_/i.test(name)
          );
        }
      );
    }

    updateSealModeBenefits() {
      const autoChargeBenefit = this.root.querySelector('[data-ms-autocharge-only]');
      if (!autoChargeBenefit) return;

      const hasNativeSellingPlan =
        this.hasSellingPlan() || Boolean(this.sealTarget?.querySelector('input[name="selling_plan"]'));
      autoChargeBenefit.hidden = !hasNativeSellingPlan;
      this.root.dataset.sealMode = hasNativeSellingPlan ? 'auto_charging' : 'recurring_invoice';
    }

    isSynchronized(mode) {
      const radio = this.getSealRadio(mode);
      if (!radio?.checked) return false;

      if (mode === 'one_time') return !this.hasSellingPlan() && !this.hasSealProperties();
      return this.hasSellingPlan() || this.hasSealProperties();
    }

    discountMatchesSeal(mode) {
      if (mode !== 'subscription') return true;

      const discountControl = this.formControls().find((control) =>
        /^properties\[_sealsub_discount_/i.test(control.name || '')
      );
      if (!discountControl) return true;

      const sealDiscount = Number.parseFloat(String(discountControl.value || '').replace(',', '.'));
      if (!Number.isFinite(sealDiscount) || Math.abs(sealDiscount - this.discount) < 0.001) return true;

      console.error(
        `[MS subscription] Discount mismatch: metafield=${this.discount}%, Seal rule=${sealDiscount}%. Checkout has been locked.`
      );
      this.showStatus('הנחת המנוי אינה מסונכרנת כרגע. יש לפנות לשירות הלקוחות.');
      return false;
    }

    canSubmit() {
      if (!this.mode || this.resyncing || !this.isSynchronized(this.mode) || !this.discountMatchesSeal(this.mode)) {
        return false;
      }

      /* Re-apply immediately before submission in case the theme or app has
       * replaced/reset the hidden variant input. */
      return this.setFormVariantForMode(this.mode);
    }

    scheduleVariantRefresh() {
      window.clearTimeout(this.variantRefreshTimer);
      this.variantRefreshTimer = window.setTimeout(async () => {
        this.refreshReferences();
        this.populateFrequencies();
        this.updateAllPrices();

        if (this.mode) await this.resyncCurrentMode();

        window.setTimeout(() => this.updateAllPrices(), 180);
      }, 40);
    }

    async resyncCurrentMode() {
      if (!this.mode) return;
      const mode = this.mode;
      const token = ++this.syncToken;
      this.resyncing = true;
      const synchronized = await this.syncSeal(mode);
      if (token !== this.syncToken || this.mode !== mode) return;
      this.resyncing = false;

      if (synchronized && this.setFormVariantForMode(mode)) {
        this.setPending(false);
        this.clearStatus();
        this.updateAllPrices();
        return;
      }

      this.mode = null;
      this.purchaseInputs.forEach((input) => (input.checked = false));
      this.frequency.disabled = true;
      this.setPending(true);
      this.showStatus('אפשרות הרכישה לא הסתנכרנה עם הווריאנט החדש. יש לבחור אותה שוב.');
      this.updateAllPrices();
    }

    selectedOptions() {
      const groups = Array.from(this.variantSelects?.querySelectorAll(':scope > .product-form__input') || []);
      return groups.map((group) => {
        const select = group.querySelector('select');
        if (select) return select.value;
        return group.querySelector('input[type="radio"]:checked')?.value || null;
      });
    }

    variantByOptions(variants, options) {
      if (!Array.isArray(variants) || !Array.isArray(options) || !options.length || options.some((value) => value == null)) {
        return null;
      }

      return variants.find(
        (variant) =>
          variant.options.length === options.length &&
          variant.options.every((option, index) => option === options[index])
      ) || null;
    }

    currentVariant() {
      const variantId = Number.parseInt(this.variantIdInput?.value, 10);
      const originalById = this.variants.find((variant) => Number(variant.id) === variantId);
      if (originalById) return originalById;

      const originalBySelectedOptions = this.variantByOptions(this.variants, this.selectedOptions());
      if (originalBySelectedOptions) return originalBySelectedOptions;

      const duplicateById = this.duplicateVariants.find((variant) => Number(variant.id) === variantId);
      const originalByDuplicateOptions = this.variantByOptions(this.variants, duplicateById?.options);
      return originalByDuplicateOptions || this.variants[0] || null;
    }

    duplicateVariant() {
      const originalVariant = this.currentVariant();
      if (!originalVariant) return null;

      return (
        this.variantByOptions(this.duplicateVariants, originalVariant.options) ||
        (this.variants.length === 1 && this.duplicateVariants.length === 1 ? this.duplicateVariants[0] : null)
      );
    }

    setFormVariantForMode(mode) {
      this.refreshReferences();
      const targetVariant = mode === 'one_time' ? this.duplicateVariant() : this.currentVariant();
      if (!this.variantIdInput || !targetVariant || targetVariant.available === false) return false;

      this.variantIdInput.value = String(targetVariant.id);
      return true;
    }

    discountedPrice(price) {
      return Math.round(Number(price || 0) * (100 - this.discount) / 100);
    }

    money(price) {
      return formatMoney(Number(price || 0), this.moneyFormat);
    }

    setText(element, value) {
      if (!element) return;
      const nextValue = String(value);
      if (element.textContent !== nextValue) element.textContent = nextValue;
    }

    updateAllPrices() {
      const variant = this.currentVariant();
      if (!variant) return;

      const basePrice = Number(variant.price || 0);
      const subscriptionPrice = this.discountedPrice(basePrice);
      this.setText(this.root.querySelector('[data-ms-one-time-price]'), this.money(basePrice));
      this.setText(this.root.querySelector('[data-ms-subscription-original-price]'), this.money(basePrice));
      this.setText(this.root.querySelector('[data-ms-subscription-price]'), this.money(subscriptionPrice));

      const readableDiscount = Number.isInteger(this.discount) ? String(this.discount) : String(this.discount).replace(/0+$/, '');
      this.setText(this.root.querySelector('[data-ms-discount-label]'), `${readableDiscount}%`);
      this.setText(this.root.querySelector('[data-ms-discount-benefit]'), `${readableDiscount}% הנחה על כל הזמנה`);

      this.updateQuantityBreakPrices();
      this.updateThemePriceContainers(variant);
    }

    updateQuantityBreakPrices() {
      if (!this.variantSelects) return;

      const groups = Array.from(this.variantSelects.querySelectorAll(':scope > .product-form__input'));
      const selected = this.selectedOptions();

      groups.forEach((group, groupIndex) => {
        if (!group.classList.contains('product-form__input--quantity-breaks')) return;

        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const optionValues = [...selected];
          optionValues[groupIndex] = input.value;
          const variant = this.variants.find((candidate) =>
            candidate.options.every((option, index) => option === optionValues[index])
          );
          if (!variant) return;

          const label = Array.from(group.querySelectorAll('label.quantity-break')).find(
            (candidate) => candidate.htmlFor === input.id
          );
          const right = label?.querySelector('.quantity-break__right');
          const currentPrice = right?.querySelector('.quantity-break__price');
          if (!right || !currentPrice) return;

          const basePrice = Number(variant.price || 0);
          const comparePrice = Number(variant.compare_at_price || 0);
          let nativeCompare = right.querySelector('.quantity-break__compare-price');
          let subscriptionOriginal = right.querySelector('.ms-subscription__qb-original');

          if (this.mode === 'subscription') {
            this.setText(currentPrice, this.money(this.discountedPrice(basePrice)));
            if (nativeCompare) nativeCompare.hidden = true;
            if (!subscriptionOriginal) {
              subscriptionOriginal = document.createElement('s');
              subscriptionOriginal.className = 'ms-subscription__qb-original';
              currentPrice.insertAdjacentElement('afterend', subscriptionOriginal);
            }
            this.setText(subscriptionOriginal, this.money(basePrice));
            subscriptionOriginal.hidden = this.discount <= 0;
            return;
          }

          this.setText(currentPrice, this.money(basePrice));
          if (subscriptionOriginal) subscriptionOriginal.hidden = true;
          if (comparePrice > basePrice) {
            if (!nativeCompare) {
              nativeCompare = document.createElement('span');
              nativeCompare.className = 'quantity-break__compare-price';
              currentPrice.insertAdjacentElement('afterend', nativeCompare);
            }
            this.setText(nativeCompare, this.money(comparePrice));
            nativeCompare.hidden = false;
          } else if (nativeCompare) {
            nativeCompare.hidden = true;
          }
        });
      });
    }

    updateThemePriceContainers(variant) {
      if (!this.section) return;

      const basePrice = Number(variant.price || 0);
      const nativeComparePrice = Number(variant.compare_at_price || 0);
      const subscriptionMode = this.mode === 'subscription';
      const displayPrice = subscriptionMode ? this.discountedPrice(basePrice) : basePrice;
      const comparePrice = subscriptionMode ? basePrice : nativeComparePrice;
      const onSale = comparePrice > displayPrice;

      this.section
        .querySelectorAll('.sticky-atc__price .price, [id^="main-price-"] .price')
        .forEach((priceElement) => {
          priceElement.classList.toggle('price--on-sale', onSale);
          const regular = priceElement.querySelector('.price__regular .price-item--regular');
          const sale = priceElement.querySelector('.price__sale .price-item--sale');
          const compare = priceElement.querySelector('.price__compare-price .price-item--regular');
          this.setText(regular, this.money(displayPrice));
          this.setText(sale, this.money(displayPrice));
          this.setText(compare, this.money(comparePrice));

          const percentage = priceElement.querySelector('.badge__percentage-saved');
          const amount = priceElement.querySelector('.badge__money-saved');
          this.setText(percentage, onSale ? String(Math.floor((comparePrice - displayPrice) * 100 / comparePrice)) : '0');
          this.setText(amount, onSale ? this.money(comparePrice - displayPrice) : this.money(0));
        });

      this.section.querySelectorAll('[class*="main-atc-price-span-"]').forEach((price) => {
        this.setText(price, this.money(displayPrice));
      });
    }

    setPending(isPending) {
      this.root.classList.toggle('is-pending', isPending);
      this.section?.classList.toggle('ms-subscription-pending', isPending);
    }

    showStatus(message) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.hidden = false;
    }

    clearStatus() {
      if (!this.status) return;
      this.status.textContent = '';
      this.status.hidden = true;
    }
  }

  const initAll = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => new MsSubscriptionWidget(root));
  };

  if (!window.MsSubscriptionWidget) {
    window.MsSubscriptionWidget = { initAll };
    document.addEventListener('sealsubs:subscription_widget_created', () => {
      initAll();
      instances.forEach((instance) => instance.connectSeal());
    });
    document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }
})();
